import React, { useState } from 'react';
import { updateMissionProgress } from '../../../../services/firebase';
import { resolveStageMission } from '../../classroom/missionLayers';

interface MissionItem {
  id: string;
  text: string;
  type?: string;
  completed?: boolean;
  gameId?: string;
}

interface MissionsPanelProps {
  squadId: string;
  levelId: string;
  missions: MissionItem[];
  getActivity: (missionId: string) => any;
  isDone: (missionId: string) => boolean;
  isNow: (missionId: string, index: number) => boolean;
  canToggleComplete: boolean;
  extractDuration: (text: string) => string | null;
  stripDuration: (text: string) => string;
  onOpenNotes: (missionId: string) => void;
  revealReadyLabel?: string | null;
  onOpenReveal?: () => void;
}

/**
 * Lesson-loop checklist. Curriculum is presented in Slides; each row opens teacher notes.
 */
const MissionsPanel: React.FC<MissionsPanelProps> = ({
  squadId,
  levelId,
  missions,
  getActivity,
  isDone,
  isNow,
  canToggleComplete,
  extractDuration,
  stripDuration,
  onOpenNotes,
  revealReadyLabel,
  onOpenReveal,
}) => {
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleToggleComplete = async (e: React.MouseEvent, missionId: string) => {
    e.stopPropagation();
    if (!canToggleComplete || togglingId) return;
    setTogglingId(missionId);
    try {
      await updateMissionProgress(squadId, levelId, missionId, 'toggle');
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not update mission status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="cd-missions-panel">
      <div className="cd-col-label">Missions</div>

      {missions.length === 0 ? (
        <div className="cd-act">
          <span className="cd-act-t">No missions listed for this class yet</span>
        </div>
      ) : (
        missions.map((mission, idx) => {
          const done = isDone(mission.id);
          const now = isNow(mission.id, idx);
          const resolved = resolveStageMission(
            { id: mission.id, text: mission.text, gameId: mission.gameId },
            getActivity(mission.id)
          );
          const dur =
            resolved.plannedMinutes != null
              ? `${resolved.plannedMinutes} min`
              : extractDuration(mission.text);
          const title = stripDuration(mission.text) || resolved.title;
          const isReveal = resolved.missionType === 'reveal';

          return (
            <div
              key={mission.id || idx}
              className={`cd-act ${done ? 'is-done' : ''} ${now ? 'is-now' : ''}`}
            >
              <div className="cd-act-row">
                <button
                  type="button"
                  className={`cd-act-ico cd-act-check ${done ? 'is-checked' : ''}`}
                  onClick={(e) => handleToggleComplete(e, mission.id)}
                  disabled={!canToggleComplete || togglingId === mission.id}
                  title={
                    canToggleComplete
                      ? done
                        ? 'Mark incomplete'
                        : 'Mark complete'
                      : 'Read-only for completed classes'
                  }
                  aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                >
                  {done ? '✓' : now ? '▸' : '○'}
                </button>
                <button
                  type="button"
                  className="cd-act-run-target"
                  onClick={() => isReveal && onOpenReveal ? onOpenReveal() : onOpenNotes(mission.id)}
                  title={isReveal ? 'Open Reveal & Review' : 'Open teaching notes'}
                >
                  <span className="cd-act-t">{title}</span>
                  <span className="cd-act-dur">{isReveal && revealReadyLabel ? revealReadyLabel : now ? 'Now' : dur || ''}</span>
                </button>
                <button
                  type="button"
                  className="cd-btn cd-btn-compact cd-act-stage-btn"
                  onClick={() => isReveal && onOpenReveal ? onOpenReveal() : onOpenNotes(mission.id)}
                >
                  {isReveal ? 'Open' : 'Notes'}
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default MissionsPanel;
